import {
  buildGenericInboxItems,
  loadGenericModuleData,
  MODULE_BUILDER_CONFIGS,
} from './builders/genericModuleBuilder';
import i18n from '../../i18n';
import localApiAdapter from '../../core/services/localApiAdapter';
import { formatBrandMoney } from '../../shared/utils/brandCurrency';

const ip = (key, opts) => i18n.t(`modules.shared.aiAgent.inboxBuilder.${key}`, opts);

const INBOX_PRIORITY = {
  HIGH: 'high',
  MEDIUM: 'medium',
};

function parseAmount(value) {
  const amount = parseFloat(value);
  return Number.isFinite(amount) ? amount : 0;
}

function daysOverdue(doc) {
  if (doc.daysOverdue != null) return Number(doc.daysOverdue) || 0;
  if (doc.days_overdue != null) return Number(doc.days_overdue) || 0;

  const due = doc.dueDate || doc.due_date;
  if (!due) return 0;

  const dueDate = new Date(due);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  dueDate.setHours(0, 0, 0, 0);
  if (Number.isNaN(dueDate.getTime())) return 0;

  return Math.max(0, Math.floor((today - dueDate) / (1000 * 60 * 60 * 24)));
}

function isUnpaidDocument(doc) {
  const paymentStatus = String(doc.paymentStatus || doc.payment_status || '').toLowerCase();
  if (['unpaid', 'partial', 'pending', 'overdue'].includes(paymentStatus)) {
    return true;
  }
  const status = String(doc.status || '').toLowerCase();
  return status === 'overdue' || status === 'pending';
}

function isDraftInvoice(doc) {
  const status = String(doc.status || '').toLowerCase();
  const type = String(doc.type || '').toLowerCase();
  const isInvoice = type === 'invoice' || type === 'facture';
  return isInvoice && (status === 'draft' || status === 'brouillon');
}

function isSameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function isTomorrow(dateValue) {
  if (!dateValue) return false;
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return false;

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);
  return isSameDay(date, tomorrow);
}

async function loadFacturationInboxData() {
  const [relances, documents] = await Promise.all([
    localApiAdapter.get('/facturation/relances').catch(() => []),
    localApiAdapter.documents?.getDocuments?.().catch(() => []),
  ]);

  return {
    relances: Array.isArray(relances) ? relances : [],
    documents: Array.isArray(documents) ? documents : [],
  };
}

function buildFacturationInboxItems({ relances, documents }) {
  const items = [];

  const overdueDocs = documents.filter(isUnpaidDocument).filter((doc) => daysOverdue(doc) > 30);
  if (overdueDocs.length > 0) {
    const clientNames = new Set(
      overdueDocs
        .map((doc) => doc.clientName || doc.client_name)
        .filter(Boolean),
    );
    const totalAmount = overdueDocs.reduce(
      (sum, doc) => sum + parseAmount(doc.amount || doc.total || doc.total_ttc),
      0,
    );

    items.push({
      id: 'unpaid-over-30',
      icon: 'alert',
      iconTone: 'orange',
      title: ip(overdueDocs.length > 1 ? 'overdue_title_other' : 'overdue_title_one', {
        count: overdueDocs.length,
      }),
      subtitle: ip(
        (clientNames.size || overdueDocs.length) > 1 ? 'overdue_subtitle_other' : 'overdue_subtitle_one',
        {
          clients: clientNames.size || overdueDocs.length,
          amount: formatBrandMoney(totalAmount),
        },
      ),
      priority: INBOX_PRIORITY.HIGH,
      priorityLabel: ip('priority_high'),
      urgencySegments: 5,
      primaryAction: true,
      prompt: ip('overdue_prompt', { count: overdueDocs.length }),
    });
  }

  const draftInvoices = documents.filter(isDraftInvoice);
  if (draftInvoices.length > 0) {
    const totalAmount = draftInvoices.reduce(
      (sum, doc) => sum + parseAmount(doc.amount || doc.total || doc.total_ttc),
      0,
    );

    items.push({
      id: 'draft-invoices',
      icon: 'document',
      iconTone: 'blue',
      title: ip(draftInvoices.length > 1 ? 'draft_title_other' : 'draft_title_one', {
        count: draftInvoices.length,
      }),
      subtitle: ip('draft_subtitle', { amount: formatBrandMoney(totalAmount) }),
      priority: INBOX_PRIORITY.MEDIUM,
      priorityLabel: ip('priority_medium'),
      urgencySegments: 3,
      primaryAction: false,
      prompt: ip('draft_prompt', { count: draftInvoices.length }),
    });
  }

  const dueTomorrowRelances = relances.filter((relance) => {
    const reminderDate = relance.reminder_date || relance.reminderDate;
    const status = String(relance.status || '').toLowerCase();
    const isActive = !status || ['pending', 'scheduled', 'planned', 'programmée', 'programmee', 'active'].includes(status);
    return isActive && isTomorrow(reminderDate);
  });

  if (dueTomorrowRelances.length > 0) {
    items.push({
      id: 'relances-tomorrow',
      icon: 'clock',
      iconTone: 'purple',
      title: ip('relance_title'),
      subtitle: ip(
        dueTomorrowRelances.length > 1 ? 'relance_subtitle_other' : 'relance_subtitle_one',
        { count: dueTomorrowRelances.length },
      ),
      priority: INBOX_PRIORITY.MEDIUM,
      priorityLabel: ip('priority_medium'),
      urgencySegments: 3,
      primaryAction: false,
      prompt: ip('relance_prompt', { count: dueTomorrowRelances.length }),
    });
  }

  return items;
}

const MODULE_INBOX_BUILDERS = {
  facturation: async () => {
    const data = await loadFacturationInboxData();
    return buildFacturationInboxItems(data);
  },
};

Object.keys(MODULE_BUILDER_CONFIGS).forEach((moduleId) => {
  MODULE_INBOX_BUILDERS[moduleId] = async () => {
    const loaded = await loadGenericModuleData(moduleId);
    return buildGenericInboxItems(loaded);
  };
});

export async function buildAgentInboxItems(moduleId) {
  const builder = MODULE_INBOX_BUILDERS[moduleId];
  if (!builder) return [];

  try {
    return await builder();
  } catch (error) {
    console.error('AI Agent: failed to build inbox items', error);
    return [];
  }
}
